import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_URL } from "src/Utils/API";
import { restaurantKeys } from "src/data/restaurant/queries/restaurant";
import { RestaurantSubscription } from "src/business/subscription/subscriptionTier";
import { ApiResponse } from "src/business/api";

type ApiResponseV2<T> = {
  data: T;
};

const subscriptionTierKeys = {
  detail: (restaurantId: string) =>
    ["subscription-tier", restaurantId] as const,
};

// GET /web/restaurants/{restaurantId}/subscription-tier
// Returns the restaurant's current subscription: tier id, auto-renewal flag,
// and next billing date. A 404 means the restaurant has no subscription tier
// yet and resolves to `null` (not an error).
export const useSubscriptionTier = (restaurantId: string) =>
  useQuery({
    queryKey: subscriptionTierKeys.detail(restaurantId),
    queryFn: async (): Promise<RestaurantSubscription | null> => {
      const res = await fetch(
        `${API_URL}/restaurants/${restaurantId}/subscription-tier`,
        { credentials: "include" },
      );
      if (res.status === 404) {
        return null;
      }
      if (!res.ok) {
        throw new Error("Failed to fetch subscription tier");
      }
      const body: ApiResponseV2<RestaurantSubscription> = await res.json();
      return body.data;
    },
    enabled: !!restaurantId,
  });

// DELETE /web/restaurants/{restaurantId}/subscription-tier
// Unsubscribes the restaurant: strips the capabilities the subscription granted
// and deletes its subscription tier record. The organization link and the staff
// assignments are deliberately left intact, so the owner keeps the restaurant,
// its payment history and the ability to pay for reinstatement. Stricter access
// than read/update — admin, the organization owner, or a staff holding
// `organization_update`.
//
// The restaurant drops out of `GET /web/restaurants` (that list is filtered on an
// active subscription), so the cached list is invalidated alongside the tier.
export const useUnsubscribeRestaurant = (
  restaurantId: string,
  onSuccess?: () => void,
  onError?: (error: Error) => void,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<void> => {
      const res = await fetch(
        `${API_URL}/restaurants/${restaurantId}/subscription-tier`,
        { method: "DELETE", credentials: "include" },
      );
      const body: ApiResponse = await res.json();
      if (!body.success) {
        throw new Error(body.message ?? "Failed to unsubscribe");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: subscriptionTierKeys.detail(restaurantId),
      });
      queryClient.invalidateQueries({ queryKey: restaurantKeys.list });
      onSuccess?.();
    },
    onError,
  });
};
